import {
  type FocusEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  Circle,
  Clock,
  RotateCcw,
  Save,
  Send,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import type {
  TaskSignatureBypassResponse,
  TaskSignatureResponse,
  TaskStatus,
} from '../../services/api/types.gen';
import { getStatusBadgeVariant, getStatusLabel } from './taskStatus';
import { computeSignatureSummary } from '../signing/signatureSummary';
import { SignatureSummaryTooltip } from '../signing/SignatureSummaryTooltip';

export interface TaskHeaderSecondaryAction {
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}

interface TaskHeaderControlsProps {
  statusItems?: ReactNode;
  primaryActions?: ReactNode;
  secondaryActions?: TaskHeaderSecondaryAction[];
  className?: string;
}

const joinClassNames = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const STATUS_ICON_BY_STATUS: Record<TaskStatus, LucideIcon> = {
  not_started: Circle,
  saved: Save,
  returned_for_review: Undo2,
  submitted_with_signature: CheckCircle,
  submitted: Send,
  pending_clinician_signature: Clock,
  completed: CheckCircle,
  exported: CheckCircle,
  reopened: RotateCcw,
  sent_to_physician: Send,
  returned_with_signature: CheckCircle,
  sent_no_signature_required: CheckCircle,
};

const STATUS_TOOLTIP_TEXT: Record<TaskStatus, string> = {
  not_started: 'Work has not started yet.',
  saved: 'Draft changes have been saved but not submitted.',
  returned_for_review:
    'Returned by QA for corrections. Edit, then sign or submit again.',
  submitted_with_signature: 'Submitted with an active signature.',
  submitted: 'Submitted for review and waiting for signature.',
  pending_clinician_signature:
    'Waiting for the clinician signature needed to complete this task.',
  completed: 'All required signatures or approvals are complete.',
  exported: 'Completed and exported.',
  reopened:
    'Reopened for editing after completion. Edits will move it back to Saved.',
  sent_to_physician: 'Sent to the physician and waiting for return.',
  returned_with_signature: 'Returned with a physician signature.',
  sent_no_signature_required:
    'Sent — this order type does not require a physician signature.',
};

// Added under the stage's own explanation rather than replacing it: the stage
// still says where the chart is, and this says which form it is.
const MISSED_VISIT_TOOLTIP_TEXT =
  'This visit was marked missed, so the missed-visit form stands in for the note.';

/**
 * Hover-on-desktop / tap-on-touch status detail panel.
 *
 * Follows `PayerReasonBadge` / `ShelfHeading`: Popover (not Tooltip) so the
 * signature summary stays reachable on mobile web, where there is no mouseover.
 * Hover-capable devices open on mouseenter with a 150ms close grace; click is
 * open-only (never toggles closed) so a touchscreen laptop's emulated
 * mouseenter-then-click sequence does not flash the panel shut. Coarse
 * pointers keep Radix's native tap toggle. `aria-describedby` + `role="tooltip"`
 * restore the old Tooltip announcement of panel copy on focus.
 */
function StatusDetailPopover({
  trigger,
  children,
}: {
  trigger: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // Evaluated once per mount — a device's hover/pointer capability does not
  // change mid-session.
  const [hoverCapable] = useState(
    () => window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
  const contentId = useId();
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    clearCloseTimeout();
    setOpen(true);
  }, [clearCloseTimeout]);

  const closeSoon = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, 150);
  }, [clearCloseTimeout]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) clearCloseTimeout();
    },
    [clearCloseTimeout]
  );

  useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  const hoverHandlers = hoverCapable
    ? { onMouseEnter: openNow, onMouseLeave: closeSoon }
    : {};

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-describedby={open ? contentId : undefined}
          className='inline-flex cursor-help rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
          onFocus={(e: FocusEvent<HTMLButtonElement>) => {
            if (e.currentTarget.matches(':focus-visible')) openNow();
          }}
          onBlur={closeSoon}
          {...(hoverCapable
            ? {
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                  // Suppress Radix's native click-toggle
                  // (composeEventHandlers checks defaultPrevented) so click is
                  // open-only — hover already owns opening and closing. Matches
                  // PayerReasonBadge: openNow, not toggle, avoids the
                  // mouseenter-then-click flash on hover+fine touch devices.
                  e.preventDefault();
                  openNow();
                },
              }
            : {})}
          {...hoverHandlers}
        >
          {trigger}
        </button>
      </PopoverTrigger>
      <PopoverContent
        id={contentId}
        role='tooltip'
        side='bottom'
        align='start'
        className='w-auto max-w-xs p-3 text-xs'
        onOpenAutoFocus={e => e.preventDefault()}
        {...hoverHandlers}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

interface TaskWorkflowStatusBadgeProps {
  status: TaskStatus;
  /**
   * Active (non-invalidated) signatures. When provided, the signed/awaiting
   * summary is folded into this pill's detail panel instead of standalone pills
   * (ROG-2842), keeping the task header compact.
   */
  signatures?: TaskSignatureResponse[];
  /**
   * Forfeited clinician signatures (ROG-3269). Passed through so a bypassed
   * chart's detail panel says so instead of reading as fully signed.
   */
  bypasses?: TaskSignatureBypassResponse[];
  /**
   * The visit's missed-visit record, when it has one. Renames the pill into
   * the vocabulary agencies read in Kinnser, where a missed visit keeps its
   * stage and carries an (MV) marker, and its `approved` flag decides which of
   * the two finished labels applies. Optional because only visits can be
   * missed: orders, communications and misc tasks leave it unset and are
   * unaffected.
   */
  missedVisitData?: { approved?: boolean | null } | null;
}

export function TaskWorkflowStatusBadge({
  status,
  signatures,
  bypasses,
  missedVisitData,
}: TaskWorkflowStatusBadgeProps) {
  const StatusIcon = STATUS_ICON_BY_STATUS[status] ?? AlertCircle;
  const signatureSummary = signatures
    ? computeSignatureSummary(signatures, status, bypasses)
    : null;

  return (
    <StatusDetailPopover
      trigger={
        <Badge
          variant={getStatusBadgeVariant(status)}
          className='flex items-center gap-1.5 text-sm'
        >
          <StatusIcon className='h-3.5 w-3.5' aria-hidden='true' />
          {getStatusLabel(status, missedVisitData)}
        </Badge>
      }
    >
      <div className='space-y-2'>
        <p>{STATUS_TOOLTIP_TEXT[status] ?? 'Current task workflow status.'}</p>
        {missedVisitData && <p>{MISSED_VISIT_TOOLTIP_TEXT}</p>}
        {signatureSummary && (
          <div className='border-t pt-1.5'>
            <SignatureSummaryTooltip summary={signatureSummary} />
          </div>
        )}
      </div>
    </StatusDetailPopover>
  );
}

export function TaskHeaderControls({
  statusItems,
  primaryActions,
  secondaryActions = [],
  className,
}: TaskHeaderControlsProps) {
  const hasPrimaryActions = Boolean(primaryActions);
  const hasSecondaryActions = secondaryActions.length > 0;
  const hasAnyActions = hasPrimaryActions || hasSecondaryActions;

  return (
    <div
      className={joinClassNames(
        'flex min-w-0 items-center justify-end gap-2 flex-wrap',
        className
      )}
    >
      {statusItems && (
        <div
          className={joinClassNames(
            'flex items-center gap-2 flex-wrap justify-end',
            hasAnyActions && 'pr-2 border-r'
          )}
        >
          {statusItems}
        </div>
      )}

      {hasPrimaryActions && (
        <div className='flex items-center gap-2 flex-wrap justify-end'>
          {primaryActions}
        </div>
      )}

      {hasSecondaryActions && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size='sm'
              variant='outline'
              className='flex items-center gap-1.5'
            >
              Actions
              <ChevronDown className='h-3.5 w-3.5' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {secondaryActions.map(action => (
              <DropdownMenuItem
                key={action.label}
                onSelect={action.onSelect}
                disabled={action.disabled}
              >
                {action.icon}
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
